import { useEffect, useState } from 'react'

import { captureException } from '@/src/config/sentry'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { usePostDeviceMutation } from '@/src/features/notification/services/usePostDeviceMutation'
import { subscribePush } from '@/src/features/notification/utils/subscribePush'

import { Cookie } from '../utils/cookies/Cookie'
import { swMessages, WINDOW_MESSAGE_TYPE, type TWindowMessage } from './messages'
import { getNotificationsAvailable, registerServiceWorker, sendMessageToSW } from './registerServiceWorker'
import { usePageVisible } from './usePageVisible'

const onHandleMessage = (event: MessageEvent<TWindowMessage>) => {
  console.debug(
    `📥 Window received message: ${event?.data?.type}.`,
    ...(typeof event.data === 'object' && 'payload' in event.data ? [{ payload: event.data.payload }] : []),
  )
  switch (event?.data?.type) {
    case WINDOW_MESSAGE_TYPE.RELOAD_IF_INVISIBLE: {
      const isInvisible = document.visibilityState !== 'visible'
      console.debug('⏭️ Reload if invisible', isInvisible ? '(invisible: reload)' : '(visible: no reload)')
      if (isInvisible) {
        window.location.reload()
      }
      break
    }
    case WINDOW_MESSAGE_TYPE.CAPTURE_EXCEPTION: {
      const { payload } = event.data
      console.error('❌ Error captured', payload)
      if (!payload.options.rate || Math.random() < payload.options.rate) {
        captureException.then((f) =>
          f(
            payload.error,
            payload.options.swName
              ? {
                  contexts: { serviceWorker: { name: payload.options.swName } },
                  extra: { details: payload.options.details },
                }
              : undefined,
          ),
        )
      }
      break
    }
  }
}

const onNewController = async () => {
  const controllerName = await sendMessageToSW.controller(swMessages.getVersion())
  console.debug('🔁 New service worker in control:', controllerName)
}

export const useServiceWorker = () => {
  const [isSwInitialized, setIsSwInitialized] = useState<boolean>(false)
  const [isControlled, setIsControlled] = useState(!!navigator.serviceWorker?.controller)
  const { mutate: registerPushSubscription } = usePostDeviceMutation()
  const isLoggedIn = getIsLoggedIn()

  const pageVisible = usePageVisible()
  const shouldCheckSWUpdate = isSwInitialized && pageVisible && isControlled

  // Update push subscription
  useEffect(() => {
    if (
      shouldCheckSWUpdate &&
      Cookie.get('deviceID') !== undefined &&
      getNotificationsAvailable() &&
      Notification.permission === 'granted'
    ) {
      console.debug('⏭️ Updating push subscription...')
      subscribePush().then((pushSubscription) => {
        if (!pushSubscription) return
        console.debug('✅ New push subscription received.', pushSubscription)
        registerPushSubscription(pushSubscription)
      })
    }
  }, [shouldCheckSWUpdate, registerPushSubscription])

  useEffect(() => {
    if (!isLoggedIn) return
    if (!('serviceWorker' in navigator)) {
      console.warn('🛑 Service workers not supported in this browser.')
      return
    }
    registerServiceWorker()
      .then(({ registration }) => {
        setIsSwInitialized(() => true)
        if (registration) setIsControlled(!!navigator.serviceWorker?.controller)
      })
      .catch((error: Error) => console.error('❌ Failed to register SW', error))
  }, [isLoggedIn])

  useEffect(() => {
    if (!shouldCheckSWUpdate) return

    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration) {
        console.warn('❌ No service worker registered')
        return
      }

      console.debug('🔍 Checking for a service worker update...')

      registration
        .update()
        .then(() => console.debug('✅ Service worker update successfully checked.'))
        .catch((error) => console.error('❌ Error while checking for update', error))
    })
  }, [shouldCheckSWUpdate])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.warn('🛑 Service workers not supported in this browser.')
      return
    }
    navigator.serviceWorker.addEventListener('controllerchange', onNewController)
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onNewController)
  }, [])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.warn('🛑 Service workers not supported in this browser.')
      return
    }
    navigator.serviceWorker.addEventListener('message', onHandleMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onHandleMessage)
  }, [])

  return { swInitialized: isSwInitialized }
}
